import { useState, useEffect } from "react";
import {
  Box,
  MenuItem,
  Select,
  Typography,
  SelectChangeEvent,
  Paper,
  Skeleton,
  Grid,
} from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import { MonthlyData } from "./types";
import { getAvailableMonths, getMonthDashboard } from "services/execReportService";
import MonthlyOverview from "./sections/MonthlyOverview";
import {
  PageDescription,
  PageTitle,
  PageTopSection,
} from "utility/styled-components";

const CostDashboard = () => {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [months, setMonths] = useState<{ value: string; label: string }[]>([]);
  const [tdspData, setTdspData] = useState<MonthlyData | null>(null);
  const [enclaveData, setEnclaveData] = useState<MonthlyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAvailableMonths().then((res) => {
      const formatted = res.months.map((m) => {
        const [y, mo] = m.split("-");
        const date = new Date(Number(y), Number(mo) - 1);
        return { value: m, label: date.toLocaleString("default", { month: "long", year: "numeric" }) };
      });
      setMonths(formatted);
      if (formatted.length > 0) {
        setSelectedMonth(formatted[0].value);
      } else {
        setLoading(false);
      }
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedMonth) return;
    setLoading(true);
    Promise.all([
      getMonthDashboard(selectedMonth, "TDSP").catch(() => null),
      getMonthDashboard(selectedMonth, "ENCLAVE").catch(() => null),
    ]).then(([tdsp, enclave]) => {
      setTdspData(tdsp);
      setEnclaveData(enclave);
    }).finally(() => setLoading(false));
  }, [selectedMonth]);

  const handleMonthChange = (e: SelectChangeEvent) => {
    setSelectedMonth(e.target.value);
  };

  const renderSection = () => {
    if (!tdspData && !enclaveData) return null;
    return (
      <MonthlyOverview
        tdspData={tdspData}
        enclaveData={enclaveData}
        selectedMonth={selectedMonth}
      />
    );
  };

  // Admin check disabled for now
  // if (!isRoleAdmin(accessToken)) {
  //   return (
  //     <Box sx={{ p: 4, textAlign: "center", minHeight: "calc(100vh - 64px)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
  //       <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Access Denied</Typography>
  //       <Typography variant="body1" color="text.secondary">You do not have permission to view the Platform Dashboard.</Typography>
  //     </Box>
  //   );
  // }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, lg: 3 }, minHeight: "calc(100vh - 64px)", bgcolor: "#F8FAFC" }}>
      {/* Header */}
      <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: { xs: 2, lg: 3 } }}
        >
          <PageTopSection>
            <PageTitle>
              Platform Dashboard {months.find((m) => m.value === selectedMonth)?.label || ""}
            </PageTitle>
            <PageDescription>
              Monitor platform costs, usage, and operational metrics
            </PageDescription>
          </PageTopSection>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                border: "1px solid #E8EBF0",
                bgcolor: "#fff",
              }}
            >
              <CalendarMonth sx={{ fontSize: 18, color: "#8A92A6" }} />
              <Box>
                <Typography variant="caption" sx={{ color: "#8A92A6", fontSize: "0.65rem", display: "block", lineHeight: 1 }}>
                  Month
                </Typography>
                <Select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  variant="standard"
                  disableUnderline
                  sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#1B2559", minWidth: 130, "& .MuiSelect-select": { py: 0 } }}
                >
                  {months.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Active section */}
        {loading ? (
          <Box>
            {/* Skeleton tabs */}
            <Skeleton variant="rounded" width="100%" height={48} sx={{ mb: 3, borderRadius: 2 }} />
            {/* Skeleton KPI cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[...Array(4)].map((_, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
            {/* Skeleton second row */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[...Array(4)].map((_, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
            {/* Skeleton operational metrics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[...Array(6)].map((_, i) => (
                <Grid item xs={6} sm={4} md={2} key={i}>
                  <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
            {/* Skeleton charts */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
              </Grid>
            </Grid>
          </Box>
        ) : (tdspData || enclaveData) ? (
          renderSection()
        ) : (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}
    </Box>
  );
};

export default CostDashboard;
